/** Get the position of the draggable dot (the point the curve passes through) */
export const getWallDotPosition = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  curvature: number,
  curvatureCenter: number = 0.5
): { x: number; y: number } => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const px = x1 + curvatureCenter * dx;
  const py = y1 + curvatureCenter * dy;
  if (len === 0) return { x: px, y: py };
  const nx = -dy / len;
  const ny = dx / len;
  return {
    x: px + nx * curvature,
    y: py + ny * curvature
  };
};

/**
 * Compute the quadratic bezier control point so that the curve passes through
 * the dot position at t=0.5 (the curve peak).
 * B(0.5) = 0.25*P0 + 0.5*C + 0.25*P1 = dot → C = 2*dot - 0.5*P0 - 0.5*P1
 *
 * curvatureCenter shifts the control point along the wall to make the curve
 * asymmetric, but the peak perpendicular displacement always equals curvature.
 */
export const getWallControlPoint = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  curvature: number,
  curvatureCenter: number = 0.5
): { cx: number; cy: number } => {
  const dot = getWallDotPosition(x1, y1, x2, y2, curvature, curvatureCenter);
  return {
    cx: 2 * dot.x - 0.5 * x1 - 0.5 * x2,
    cy: 2 * dot.y - 0.5 * y1 - 0.5 * y2
  };
};

/** Evaluate a point on quadratic bezier at parameter t (0-1) */
export const quadraticBezierPoint = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number,
  t: number
): { x: number; y: number } => {
  const mt = 1 - t;
  return {
    x: mt * mt * x1 + 2 * mt * t * cx + t * t * x2,
    y: mt * mt * y1 + 2 * mt * t * cy + t * t * y2
  };
};

/** Sample N points along the bezier curve. Returns flat [x0,y0, x1,y1, ...] array for Konva Line points */
export const sampleBezierPoints = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number,
  numPoints: number
): number[] => {
  const pts: number[] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const p = quadraticBezierPoint(x1, y1, cx, cy, x2, y2, t);
    pts.push(p.x, p.y);
  }
  return pts;
};

/** Sample points along a sub-range [tStart, tEnd] of the bezier. Returns flat array for Konva Line */
export const sampleBezierSubRange = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number,
  tStart: number,
  tEnd: number,
  numPoints: number
): number[] => {
  const pts: number[] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = tStart + (tEnd - tStart) * (i / numPoints);
    const p = quadraticBezierPoint(x1, y1, cx, cy, x2, y2, t);
    pts.push(p.x, p.y);
  }
  return pts;
};

/** Compute approximate arc length of quadratic bezier using numerical integration */
export const bezierArcLength = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number
): number => {
  const steps = 64;
  let length = 0;
  let prev = { x: x1, y: y1 };
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const curr = quadraticBezierPoint(x1, y1, cx, cy, x2, y2, t);
    const segDx = curr.x - prev.x;
    const segDy = curr.y - prev.y;
    length += Math.sqrt(segDx * segDx + segDy * segDy);
    prev = curr;
  }
  return length;
};

/** Find the bezier parameter t for a given arc-length distance along the curve */
export const bezierParameterAtDistance = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number,
  distance: number
): number => {
  const steps = 64;
  let accumulated = 0;
  let prev = { x: x1, y: y1 };
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const curr = quadraticBezierPoint(x1, y1, cx, cy, x2, y2, t);
    const segDx = curr.x - prev.x;
    const segDy = curr.y - prev.y;
    const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
    if (accumulated + segLen >= distance) {
      const frac = segLen > 0 ? (distance - accumulated) / segLen : 0;
      return (i - 1) / steps + frac / steps;
    }
    accumulated += segLen;
    prev = curr;
  }
  return 1;
};

/** Get point and perpendicular normal at parameter t on the bezier */
export const bezierPointAndNormal = (
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number,
  t: number
): { x: number; y: number; nx: number; ny: number } => {
  const p = quadraticBezierPoint(x1, y1, cx, cy, x2, y2, t);
  // Derivative of quadratic bezier: B'(t) = 2(1-t)(C-P0) + 2t(P1-C)
  const dtx = 2 * (1 - t) * (cx - x1) + 2 * t * (x2 - cx);
  const dty = 2 * (1 - t) * (cy - y1) + 2 * t * (y2 - cy);
  const dLen = Math.sqrt(dtx * dtx + dty * dty);
  if (dLen === 0) return { ...p, nx: 0, ny: -1 };
  // Normal is perpendicular to tangent (rotated 90 degrees CCW)
  return {
    ...p,
    nx: -dty / dLen,
    ny: dtx / dLen
  };
};
